import map from 'lodash/map';
import React, { memo } from 'react';
import Highlighter from 'react-highlight-words';
import { ProgressBar, Tooltip, TooltipBody, TooltipFooter, TooltipHeader } from '@wg/wows-react-uikit';

import { highlighterPercentRegexp } from '~/settings';
import { t } from '~/helpers/localization';

import BuildingIcon from '~/UIKit/BuildingIcon/BuildingIcon';

import styles from './BuildingTooltip.module.scss';

import type { ISupplyBuilding } from '~/store/slices/settingsSlice';
import type { IBattlesStats } from '~/types/declaration';

type IProps = {
  battlesStats: IBattlesStats;
  building: ISupplyBuilding;
};

const BuildingTooltip: React.FC<IProps> = ({ battlesStats, building }) => {
  const meta = building.levels[building.level].meta;

  const hasNextLevel = building.levels[building.level + 1] !== undefined;
  const nextLevelMeta = hasNextLevel ? building.levels[building.level + 1].meta : meta;

  const isShipsAndVessels = ['ships', 'vessels'].includes(building.name);
  const isSuperShipsHome = building.name === 'superships_home';

  const battleType = nextLevelMeta.levelRequirements?.type;
  const needBattles = nextLevelMeta.levelRequirements?.battleCount || 0;
  const currentBattles = (battleType && battlesStats[battleType]) || 0;
  const battleProgress = Math.min(currentBattles / needBattles, 1);

  return (
    <Tooltip>
      <TooltipHeader isBold={false} isHelpIcon={false}>
        <div className={styles.tooltipInlineHeader}>
          <BuildingIcon buildingId={building.name} />
          <div className={styles.buildingTooltipHeaderText}>
            <div className={styles.buildingTooltipModifierTitle}>{`${building.levels[building.level].title}`}</div>
            <p>
              {t('Улучшений: %(level)s из %(maxLevel)s', {
                level: building.level,
                maxLevel: building.maxLevel,
              })}
            </p>
          </div>
        </div>
      </TooltipHeader>
      <TooltipBody>{`${meta.function}`}</TooltipBody>
      {isSuperShipsHome && meta.bonus ? (
        <TooltipBody key="bonus" bgColor="dark">
          {map(building.levels, (item, idx) => {
            if (Number(idx) <= building.level && item.meta.bonus && item.meta.bonus !== '') {
              return (
                <div key={`bonus-${idx}`} className={styles.topWrapperTooltip}>
                  <Highlighter
                    highlightClassName="HighlightPercent"
                    searchWords={highlighterPercentRegexp}
                    textToHighlight={item.meta.bonus}
                  />
                </div>
              );
            }
          })}
        </TooltipBody>
      ) : (
        meta.bonus && (
          <TooltipBody key="bonus" bgColor="dark">
            <Highlighter
              highlightClassName="HighlightPercent"
              searchWords={highlighterPercentRegexp}
              textToHighlight={meta.totalBonus}
            />
          </TooltipBody>
        )
      )}

      {isShipsAndVessels && (
        <TooltipBody bgColor="dark">
          <div className={styles.tooltipProgressRow}>
            {t('Общее количество боев:')}
            <span>{t('%(currentBattles)s/%(needBattles)s', { currentBattles, needBattles })}</span>
          </div>
          <ProgressBar size={'small'} completed={battleProgress} />
        </TooltipBody>
      )}

      <TooltipFooter mouseIcon="left">{t('Перейти к строению')}</TooltipFooter>
    </Tooltip>
  );
};

export default memo(BuildingTooltip);
